import { Dispatch, RefObject, SetStateAction } from 'react';

import { Box, Center, IconButton, Spinner, Tooltip } from '@vimeo/bokeh';
import { CloseX } from '@vimeo/bokeh-icons';
import { useLayout } from '@vimeo/clip-ui/hooks/use-layout';
import { translate } from '@vimeo/i18n-runtime';
import { ESidebarAttach } from '@vimeo/live-core/components/sidebar/ResponsiveSidebar.types';
import { EComposerSessionType } from '@vimeo/live-core/types/composer_session';
import {
  InteractionToolsRegistrantObserver,
  InteractionToolsStatusObserver,
} from '@vimeo/live-interaction';
import { InteractionToolsEntry } from '@vimeo/live-interaction';

import { useLiveEngagementToolModule } from '../hooks/use-live-engagement-tool-module';
import { useLiveEventData } from '../hooks/use-live-event-data';

interface LiveEngagementToolModuleProps {
  /** The Clip Chat Room ID */
  roomId: number;
  /** Whether the event is currently loading */
  isEventLoading?: boolean;
  /** Whether the live engagement tool module is used on a mobile drawer view */
  isMobileDrawer?: boolean;
  /** Reference to the player container element */
  playerContainerRef: RefObject<HTMLDivElement>;
  /** Function to set the interaction tools enabled state. */
  setIsInteractionToolsEnabled: Dispatch<SetStateAction<boolean>>;
}

export const LiveEngagementToolModule = ({
  roomId,
  isEventLoading,
  isMobileDrawer,
  playerContainerRef,
  setIsInteractionToolsEnabled,
}: LiveEngagementToolModuleProps) => {
  const { associatedShowcaseId, eventId, eventHash, hasRegistration } = useLiveEventData();
  const { isLiveToolsSideModuleOpen, liveToolsMobileDrawerStyles, roomHashedPassword } =
    useLiveEngagementToolModule({
      roomId,
      associatedShowcaseId,
      eventId,
      isEventLoading,
      playerContainerRef,
      setIsInteractionToolsEnabled,
    });

  if (!eventId) {
    return null;
  }

  return (
    <InteractionToolsStatusObserver
      sessionType={EComposerSessionType.LIVE_EVENT}
      sessionId={eventId}
      isActive={false}
      isBypass={true}
      placeholder={isLiveToolsSideModuleOpen ? <Loader /> : null}
    >
      <>
        {isLiveToolsSideModuleOpen ? (
          <Box
            data-test-id="live-engagement-tool"
            pos="relative"
            w="100%"
            h="100%"
            borderRadius="drawer"
            overflow="hidden"
            zIndex={1} // Tuck Desktop Live Engagement Tool to be under sticky player and global nav
            {...(isMobileDrawer && liveToolsMobileDrawerStyles)}
            css={{
              '.interaction-sidebar-panel': {
                zIndex: 1, // Tuck Desktop Live Engagement Tool to be under sticky player and global nav
              },
            }}
          >
            <CloseIconButton />

            <InteractionToolsRegistrantObserver isActive={hasRegistration} sessionId={eventId}>
              <InteractionToolsEntry
                backgroundColor="surface"
                sessionType={EComposerSessionType.LIVE_EVENT}
                sessionId={eventId}
                roomHashedPassword={roomHashedPassword}
                roomUnlistedHash={eventHash || ''}
                toolbarAttachPosition={ESidebarAttach.TOP}
                isManagementDisabled={true}
                isCustomThemeEnabled={true}
                toolbarPanelIsFluid={true}
                isCollapsibleSignIn={true}
                isStandalone={true}
                isActiveStatusObservingEnabled={false}
                isBypassStatusObservingEnabled={true}
              />
            </InteractionToolsRegistrantObserver>
          </Box>
        ) : null}
      </>
    </InteractionToolsStatusObserver>
  );
};

const CloseIconButton = () => {
  const { onClose } = useLayout();

  return (
    <Tooltip label={translate('Close')} placement="top">
      <IconButton
        aria-label={translate('Close')}
        icon={<CloseX />}
        size="sm"
        variant="minimal"
        onClick={onClose}
        pos="absolute"
        top="md"
        right="md"
        zIndex={101} // Live Engagement Tool is 101
      />
    </Tooltip>
  );
};

const Loader = () => (
  <Center h="100%">
    <Spinner color="text-primary" />
  </Center>
);
